/* ============================================================ */
/* HERO PREMIUM V2.0 - MAISON LUMIEN                             */
/* ARCHIVO: assets/hero-premium.js                               */
/* VERSIÓN DEFINITIVA: Arrow functions, sin bind, robusta       */
/* ============================================================ */

(function() {
  'use strict';

  const CONFIG = {
    parallaxIntensity: 12,
    parallaxSmoothness: 0.08,
    counterDuration: 2000,
    counterIncrement: 60,
    glowIntensity: 30,
    glowSmoothness: 0.06,
    scrollThreshold: 0.3,
    desktopBreakpoint: 768,
  };

  const utils = {
    clamp: function(value, min, max) {
      return Math.min(Math.max(value, min), max);
    },
    mapRange: function(value, inMin, inMax, outMin, outMax) {
      return ((value - inMin) / (inMax - inMin)) * (outMax - outMin) + outMin;
    },
    throttle: function(func, limit) {
      let inThrottle = false;
      return function() {
        const args = arguments;
        const context = this;
        if (!inThrottle) {
          func.apply(context, args);
          inThrottle = true;
          setTimeout(() => inThrottle = false, limit);
        }
      };
    },
    debounce: function(func, delay) {
      let timeout;
      return function() {
        const args = arguments;
        const context = this;
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(context, args), delay);
      };
    },
    prefersReducedMotion: function() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    },
    isTouchDevice: function() {
      return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    }
  };

  /**
   * ============================================================
   * CLASE PRINCIPAL: HeroPremium (con arrow functions)
   * ============================================================
   */
  class HeroPremium {
    constructor(element) {
      // 1. Obtener el elemento
      this.hero = typeof element === 'string' 
        ? document.querySelector(element) 
        : element;

      if (!this.hero) {
        // No hay hero, salir sin errores
        return;
      }

      // 2. Referencias
      this.imageWrapper = this.hero.querySelector('[data-parallax]');
      this.image = this.hero.querySelector('.hero-premium-img');
      this.glowSpot = this.hero.querySelector('.hero-premium-glow-spot');
      this.statNumbers = this.hero.querySelectorAll('.stat-number[data-count]');
      this.floatingCards = this.hero.querySelectorAll('.floating-card');
      this.scrollIndicator = this.hero.querySelector('.hero-premium-scroll');

      // 3. Estado
      this.mouseX = 0;
      this.mouseY = 0;
      this.currentX = 0;
      this.currentY = 0;
      this.glowX = 0;
      this.glowY = 0;
      this.isVisible = false;
      this.rafId = null;
      this.animationFrame = null;
      this.observers = [];
      this.isDesktop = window.innerWidth > CONFIG.desktopBreakpoint;
      this.prefersReducedMotion = utils.prefersReducedMotion();

      // 4. Inicializar (no necesita bind porque usamos arrow functions)
      this.init();
    }

    /**
     * ============================================================
     * MÉTODOS CON ARROW FUNCTIONS (heredan this automáticamente)
     * ============================================================
     */

    init = () => {
      if (!this.hero) return;

      if (this.prefersReducedMotion) {
        this.setupReducedMotion();
        return;
      }

      this.initParallax();
      this.initCounterAnimation();
      this.initGlowEffect();
      this.initFloatingCards();
      this.initAccessibility();
      this.initResizeHandler();
      this.initPerformance();

      this.hero.dataset.initialized = 'true';
    };

    initParallax = () => {
      if (!this.imageWrapper || !this.isDesktop || utils.isTouchDevice()) return;
      this.hero.addEventListener('mousemove', this.onMouseMove, { passive: true });
      this.animationFrame = requestAnimationFrame(this.animate);
    };

    onMouseMove = (e) => {
      if (!this.hero) return;
      const rect = this.hero.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width - 0.5;
      const y = (e.clientY - rect.top) / rect.height - 0.5;
      this.mouseX = x * CONFIG.parallaxIntensity;
      this.mouseY = y * CONFIG.parallaxIntensity;
    };

    animate = () => {
      if (!this.hero) return;
      this.currentX += (this.mouseX - this.currentX) * CONFIG.parallaxSmoothness;
      this.currentY += (this.mouseY - this.currentY) * CONFIG.parallaxSmoothness;

      if (this.imageWrapper) {
        this.imageWrapper.style.transform = `translate(${this.currentX * -1}px, ${this.currentY * -1}px)`;
      }

      if (this.glowSpot) {
        this.glowX += (this.mouseX * 0.5 - this.glowX) * CONFIG.glowSmoothness;
        this.glowY += (this.mouseY * 0.5 - this.glowY) * CONFIG.glowSmoothness;
        this.glowSpot.style.transform = `translate(calc(-50% + ${this.glowX * 0.3}px), calc(-50% + ${this.glowY * 0.3}px))`;
        this.glowSpot.style.backgroundPosition = `${50 + this.glowX * 0.2}% ${50 + this.glowY * 0.2}%`;
      }

      this.rafId = requestAnimationFrame(this.animate);
    };

    initCounterAnimation = () => {
      if (!this.statNumbers.length) return;
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.animateCounters();
            observer.unobserve(entry.target);
          }
        });
      }, { threshold: CONFIG.scrollThreshold });

      const statsContainer = this.hero.querySelector('.hero-premium-stats');
      if (statsContainer) {
        observer.observe(statsContainer);
        this.observers.push(observer);
      }
    };

    animateCounters = () => {
      this.statNumbers.forEach(el => {
        const target = parseInt(el.getAttribute('data-count'), 10);
        if (!target || isNaN(target) || target < 0) return;
        const increment = Math.ceil(target / CONFIG.counterIncrement);
        let current = 0;
        let startTime = null;

        const step = (timestamp) => {
          if (!startTime) startTime = timestamp;
          const progress = (timestamp - startTime) / CONFIG.counterDuration;
          if (progress < 1) {
            const eased = 1 - Math.pow(1 - progress, 3);
            current = Math.min(Math.round(eased * target), target);
            el.textContent = current.toLocaleString();
            requestAnimationFrame(step);
          } else {
            el.textContent = target.toLocaleString();
          }
        };
        const delay = Array.from(this.statNumbers).indexOf(el) * 150;
        setTimeout(() => requestAnimationFrame(step), delay);
      });
    };

    initGlowEffect = () => {
      if (!this.glowSpot || !this.isDesktop || utils.isTouchDevice()) return;
      this.glowSpot.style.opacity = '0.8';
    };

    initFloatingCards = () => {
      if (!this.floatingCards.length) return;
      if (!this.prefersReducedMotion) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const card = entry.target;
              if (!card.dataset.animated) {
                card.style.animation = 'none';
                card.offsetHeight;
                card.style.animation = `heroCardFloatIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) ${card.style.getPropertyValue('--card-delay') || '0s'} forwards`;
                card.dataset.animated = 'true';
              }
              observer.unobserve(card);
            }
          });
        }, { threshold: 0.2 });
        this.floatingCards.forEach(card => {
          observer.observe(card);
          this.observers.push(observer);
        });
      }
    };

    initAccessibility = () => {
      if (!this.hero) return;
      if (!this.hero.getAttribute('role')) {
        this.hero.setAttribute('role', 'banner');
      }
      const buttons = this.hero.querySelectorAll('.hero-premium-btn');
      buttons.forEach(btn => {
        if (!btn.getAttribute('aria-label')) {
          const text = btn.querySelector('.btn-text')?.textContent || 'Botón';
          btn.setAttribute('aria-label', text);
        }
      });
      this.floatingCards.forEach(card => {
        if (!card.getAttribute('aria-label')) {
          const label = card.querySelector('.card-label')?.textContent || 'Categoría';
          card.setAttribute('aria-label', `Ir a ${label}`);
        }
      });
      if (this.scrollIndicator) {
        this.scrollIndicator.setAttribute('aria-hidden', 'true');
      }
    };

    initResizeHandler = () => {
      const handleResize = utils.debounce(() => {
        const wasDesktop = this.isDesktop;
        this.isDesktop = window.innerWidth > CONFIG.desktopBreakpoint;
        if (wasDesktop !== this.isDesktop) {
          if (this.isDesktop && !this.prefersReducedMotion) {
            this.initParallax();
          } else {
            this.destroyParallax();
          }
        }
      }, 250);
      window.addEventListener('resize', handleResize);
    };

    initPerformance = () => {
      if (!this.prefersReducedMotion && this.hero) {
        const visibilityObserver = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            this.isVisible = entry.isIntersecting;
            if (!this.isVisible && this.rafId) {
              cancelAnimationFrame(this.rafId);
              this.rafId = null;
            } else if (this.isVisible && !this.rafId) {
              this.rafId = requestAnimationFrame(this.animate);
            }
          });
        }, { threshold: 0.1 });
        visibilityObserver.observe(this.hero);
        this.observers.push(visibilityObserver);
      }
      window.addEventListener('beforeunload', () => this.destroy());
    };

    setupReducedMotion = () => {
      if (this.imageWrapper) {
        this.imageWrapper.style.transition = 'none';
        this.imageWrapper.style.transform = 'none';
      }
      this.floatingCards.forEach(card => {
        card.style.opacity = '1';
        card.style.transform = 'none';
        card.style.animation = 'none';
        card.style.transition = 'none';
      });
      if (this.glowSpot) {
        this.glowSpot.style.transition = 'none';
        this.glowSpot.style.transform = 'none';
      }
      this.statNumbers.forEach(el => {
        const count = el.getAttribute('data-count') || '0';
        el.textContent = parseInt(count, 10).toLocaleString();
      });
      if (this.hero) {
        this.hero.removeEventListener('mousemove', this.onMouseMove);
      }
    };

    destroyParallax = () => {
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
      if (this.animationFrame) {
        cancelAnimationFrame(this.animationFrame);
        this.animationFrame = null;
      }
      if (this.hero) {
        this.hero.removeEventListener('mousemove', this.onMouseMove);
      }
      if (this.imageWrapper) {
        this.imageWrapper.style.transform = 'none';
      }
      if (this.glowSpot) {
        this.glowSpot.style.transform = 'none';
      }
    };

    destroy = () => {
      this.observers.forEach(observer => observer.disconnect());
      this.observers = [];
      this.destroyParallax();
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('beforeunload', this.destroy);
    };

    restart = () => {
      this.destroy();
      this.init();
    };
  }

  /**
   * ============================================================
   * INICIALIZACIÓN SEGURA
   * ============================================================
   */

  let heroInstance = null;

  function initHero() {
    try {
      const heroElement = document.querySelector('.hero-premium');
      if (!heroElement) return;
      if (heroElement.dataset.initialized === 'true') return;
      heroInstance = new HeroPremium(heroElement);
    } catch (error) {
      // Silenciar errores para no romper la página
      console.warn('HeroPremium: Error en initHero:', error.message);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initHero);
  } else {
    // Si el DOM ya está listo, ejecutar inmediatamente
    initHero();
  }

  // Re-inicializar en secciones dinámicas (Shopify)
  if (window.Shopify && window.Shopify.sections) {
    window.Shopify.sections.observe({
      on: 'new',
      callback: function() {
        setTimeout(initHero, 100);
      }
    });
  }

  // Exponer clase para debug
  window.HeroPremium = HeroPremium;

})();